import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getSocket, subscribeSocket } from '@/app/state/socketState';
import { getWsConnectionId } from '@/app/state/wsConnectionState';
import { armStableTimer, CONNECTION_LOST_STATUSES, enterTransition } from '@/store/middlewares/transitionMiddleware';

// Absolute base in test: undici rejects relative URLs in vitest. See docs/ai/frontend.md → "Test base URL".
const baseUrl = import.meta.env.MODE === 'test' ? 'http://localhost/api/' : 'api/';

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: headers => {
    // See docs/ai/websocket-events.md → "Originator filter".
    const id = getWsConnectionId();
    if (id) headers.set('X-Ws-Connection-Id', id);
    return headers;
  },
  responseHandler: response =>
    response.headers.get('Content-Type')?.includes('application/json') ? response.json() : response.text(),
});

// See docs/ai/websocket-events.md → "Block traffic".
const waitForStable = (): Promise<void> | undefined => {
  if (!getSocket().inTransition) return undefined;
  return new Promise(resolve => {
    const unsubscribe = subscribeSocket(() => {
      if (!getSocket().inTransition) {
        unsubscribe();
        resolve();
      }
    });
  });
};

type BaseQueryResult = Awaited<ReturnType<typeof rawBaseQuery>>;

// Replay a dropped GET across the transition; mutations are never replayed. See docs/ai/websocket-events.md → "Query retry across transition".
export const withTransitionRetry = async (
  run: () => Promise<BaseQueryResult>,
  type: 'query' | 'mutation'
): Promise<BaseQueryResult> => {
  for (;;) {
    const wait = waitForStable();
    if (wait) await wait;
    const result = await run();
    if (type !== 'query' || !CONNECTION_LOST_STATUSES.has(result.error?.status)) return result;
    enterTransition();
    armStableTimer();
  }
};

const baseQuery: typeof rawBaseQuery = (args, api, extraOptions) =>
  withTransitionRetry(async () => rawBaseQuery(args, api, extraOptions), api.type);

export const emptySplitApi = createApi({
  baseQuery,
  endpoints: () => ({}),
});
